export interface RefreshRunner {
	run(task: () => Promise<void>): Promise<void>;
}

/**
 * Runs one refresh at a time and collapses a burst into the most recently
 * requested follow-up. This prevents polling and realtime events from building
 * an unbounded API backlog while still applying the newest UI state.
 */
export function createRefreshRunner(): RefreshRunner {
	let inFlight: Promise<void> | undefined;
	let queued: (() => Promise<void>) | undefined;

	async function drain(first: () => Promise<void>): Promise<void> {
		let current: (() => Promise<void>) | undefined = first;
		let firstError: unknown;
		while (current) {
			queued = undefined;
			try {
				await current();
			} catch (caught) {
				firstError ??= caught;
			}
			current = queued;
		}
		if (firstError !== undefined) throw firstError;
	}

	return {
		run(task) {
			if (inFlight) {
				queued = task;
				return inFlight;
			}
			inFlight = drain(task).finally(() => {
				inFlight = undefined;
			});
			return inFlight;
		},
	};
}
